<template>
  <CModal
    v-model:visible="showModal"
    title="Tablet History"
    size="xl"
    color="primary"
    @close="closeModal"
  >
    <CDataTable
      :items="data"
      :fields="fields"
      :pagination="false"
      :loading="loading"
      hover
      striped
      class="mb-0"
    >
      <template #created_at="{ item }">
        <td>{{ formatDate(item.created_at) }}</td>
      </template>

      <template #action="{ item }">
        <td>
          <span class="badge" :class="actionColor(item.action)">{{ item.action }}</span>
        </td>
      </template>

      <template #actor="{ item }">
        <td>
          <template v-if="item.actor">
            <strong>{{ item.actor.name }}</strong>
            <br />
            <small class="text-muted">{{ item.actor.email }}</small>
          </template>
          <span v-else class="text-muted font-italic">System</span>
        </td>
      </template>

      <template #changes="{ item }">
        <td>
          <div v-for="(change, i) in itemChanges(item)" :key="i" class="d-flex align-items-center mb-1">
            <small class="text-muted font-weight-bold mr-2">{{ change.label }}:</small>
            <span class="badge bg-light text-dark border">{{ change.from }}</span>
            <i class="fas fa-long-arrow-alt-right mx-2 text-primary" />
            <span class="badge bg-light text-dark border">{{ change.to }}</span>
          </div>
          <span v-if="!itemChanges(item).length" class="text-muted font-italic">No changes</span>
        </td>
      </template>

      <template #no-items-view>
        <div class="text-center p-3 text-muted">
          {{ loading ? 'Loading...' : 'No history found.' }}
        </div>
      </template>
    </CDataTable>

    <div v-if="lastPage > 1" class="mt-3 d-flex justify-content-end">
      <pagination :current-page="page" :last-page="lastPage" @paginate="pageChange" />
    </div>

    <template #footer>
      <CButton color="secondary" @click="closeModal">Close</CButton>
    </template>
  </CModal>
</template>

<script>
import Pagination from '@/components/Pagination.vue';

export default {
  name: 'TabletHistoryModal',
  components: { Pagination },
  data() {
    return {
      showModal: false,
      loading: false,
      tabletId: null,
      data: [],
      page: 1,
      lastPage: 1,
      fields: [
        { key: 'created_at', label: 'Date' },
        { key: 'action', label: 'Action', sorter: false },
        { key: 'origin', label: 'Origin', sorter: false },
        { key: 'actor', label: 'Actor', sorter: false },
        { key: 'changes', label: 'Changes', sorter: false },
      ],
    };
  },
  methods: {
    open(tabletId) {
      this.tabletId = tabletId;
      this.page = 1;
      this.showModal = true;
      this.fetchHistory();
    },

    closeModal() {
      this.showModal = false;
      this.data = [];
    },

    pageChange(page) {
      this.page = page;
      this.fetchHistory();
    },

    fetchHistory() {
      if (!this.tabletId) return;
      this.loading = true;
      this.$http
        .get(`/enterprise-tablets/${this.tabletId}/history`, { params: { page: this.page } })
        .then(({ data }) => {
          this.data = data.data;
          this.lastPage = data.meta?.last_page || 1;
        })
        .catch(({ response }) => {
          this.$toast.error(response?.data?.message || 'Error fetching history');
        })
        .finally(() => {
          this.loading = false;
        });
    },

    actionColor(action) {
      const map = {
        assign: 'bg-success text-white',
        unassign: 'bg-warning text-dark',
        transfer: 'bg-info text-white',
        activate: 'bg-primary text-white',
        deactivate: 'bg-secondary text-white',
        delete: 'bg-danger text-white',
      };
      return map[action] || 'bg-dark text-white';
    },

    itemChanges(item) {
      const before = item.before || {};
      const after = item.after || {};
      const changes = [];

      const beforeUser = before.user?.email || null;
      const afterUser = after.user?.email || null;
      if (beforeUser !== afterUser) {
        changes.push({ label: 'User', from: beforeUser || '—', to: afterUser || '—' });
      }
      if (Boolean(before.active) !== Boolean(after.active)) {
        changes.push({ label: 'Active', from: before.active ? 'Yes' : 'No', to: after.active ? 'Yes' : 'No' });
      }
      if (Boolean(before.online) !== Boolean(after.online)) {
        changes.push({ label: 'Online', from: before.online ? 'Yes' : 'No', to: after.online ? 'Yes' : 'No' });
      }
      if ((before.deleted_at || null) !== (after.deleted_at || null)) {
        changes.push({
          label: 'Deleted',
          from: before.deleted_at ? this.formatDate(before.deleted_at) : '—',
          to: after.deleted_at ? this.formatDate(after.deleted_at) : '—',
        });
      }

      return changes;
    },

    formatDate(value) {
      return new Date(value).toLocaleString();
    },
  },
};
</script>
